import {type ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef} from 'react'

import {composeCellKey} from '../provider/cell-key'
import {useBoardConfig, useBoardDataContext} from '../provider/contexts'
import type {CellAddress, ItemId} from '../types'
import {BoardSelectionContext, type BoardSelectionStore} from './contexts'
import {isSameSelection, nextSelection, selectionKey} from './next-selection'
import type {BoardSelectedCard} from './types'

const NO_ITEM_IDS: readonly ItemId[] = []

export type BoardSelectionProviderProps = {
  selectedCards: readonly BoardSelectedCard[]
  onSelectionChange?: (selectedCards: readonly BoardSelectedCard[]) => void
  children: ReactNode
}

/**
 * Publishes what the caller states is selected, and turns a gesture on a card into the
 * selection the caller would have next.
 *
 * It stores no selection of its own, so there is nothing here to fall out of step with
 * the caller: a board handed new data, or handed the same selection twice, answers from
 * the one list the caller stated. What it does hold is where a range runs from, which is
 * not a selected id but a fact about the gesture in progress, and the only thing the
 * caller has no way to know.
 *
 * Rendered inside the data and config contexts, because a range is over the cards one
 * cell holds and both of those are needed to say what they are. Renders no DOM, like
 * everything else in the provider.
 */
export function BoardSelectionProvider({
  selectedCards,
  onSelectionChange,
  children,
}: BoardSelectionProviderProps): ReactNode {
  const {cellsByAddress} = useBoardDataContext()
  const {getItemId} = useBoardConfig()

  const selectedKeys = useMemo(() => new Set(selectedCards.map(selectionKey)), [selectedCards])

  /*
    One cell's cards, in the order the caller stated them.

    The caller's data rather than the cell's rendered cards, and that is what keeps a
    range honest on a board that windows: a cell draws the cards that fit, so a range
    running from the first to the two hundredth covers a hundred and ninety-eight cards
    that were never in the page. Reading them off the page would silently select whatever
    happened to be drawn.

    Not the focus grid's `itemIdsAt`, though it answers the same question. Selection and
    focus move independently and are meant to keep doing so, and a shared lookup is the
    kind of coupling that only shows up when one of them changes.
  */
  const itemIdsAt = useCallback(
    (address: CellAddress): readonly ItemId[] =>
      cellsByAddress.get(composeCellKey(address))?.items.map(item => getItemId(item)) ?? NO_ITEM_IDS,
    [cellsByAddress, getItemId],
  )

  const selectedKeysRef = useRef(selectedKeys)
  const latestRef = useRef({selectedCards, onSelectionChange, itemIdsAt})
  const listenersRef = useRef<Set<() => void>>(new Set())

  /*
    One layout effect rather than two, and the order inside it is load-bearing.

    A layout effect so a card's treatment changes in the same commit the gesture was
    answered in, before anything is painted. Both refs are brought up to date before any
    listener runs, because `useBoardSelection` reads the whole list from `latestRef` while
    a card reads one key from `selectedKeysRef`: notifying between the two assignments
    would wake the toolbar with the list it already had.

    The gesture below reads through the same ref rather than closing over its arguments,
    so it answers an event with what is current at the moment of the click rather than
    what was current when the store was built. That is what lets the store keep one
    identity for the life of the board, which is what stops a card re-rendering because
    the caller rebuilt a callback.
  */
  useLayoutEffect(() => {
    selectedKeysRef.current = selectedKeys
    latestRef.current = {selectedCards, onSelectionChange, itemIdsAt}

    for (const listener of listenersRef.current) listener()
  }, [selectedKeys, selectedCards, onSelectionChange, itemIdsAt])

  // Every subscriber removes itself, so this is only for a board torn down while some of
  // them are still mounted, which is the ordinary case on unmount.
  useEffect(() => {
    const listeners = listenersRef.current

    return () => {
      listeners.clear()
    }
  }, [])

  const rangeStartRef = useRef<BoardSelectedCard | null>(null)
  // The selection the anchor above was set against, which is the last one reported. What
  // the caller states next either matches it, and the run of gestures continues, or does
  // not, and the caller has moved on.
  const reportedRef = useRef<readonly BoardSelectedCard[]>(selectedCards)

  const store = useMemo<BoardSelectionStore>(() => {
    /**
     * Hands the caller a selection, unless it is the one it already stated.
     *
     * The short-circuit is why a gesture that changed nothing reports nothing: it would
     * otherwise hand back a new array to store and re-render the board to draw exactly
     * what it was drawing.
     */
    const report = (cards: readonly BoardSelectedCard[], rangeStart: BoardSelectedCard | null): void => {
      const {selectedCards: current, onSelectionChange: onChange} = latestRef.current
      if (onChange === undefined) return

      // Where a range runs from moves even when the selection does not, because that did
      // change.
      rangeStartRef.current = rangeStart

      // Nothing to report leaves the caller holding what it already had, so that, rather
      // than the selection just built, is what the anchor now belongs to.
      if (isSameSelection(cards, current)) {
        reportedRef.current = current

        return
      }

      reportedRef.current = cards
      onChange(cards)
    }

    /**
     * Where a range runs from, or `null` because there is nowhere left to run one from.
     *
     * The anchor is a fact about a run of gestures, and a run lasts only while the caller
     * is still holding what the last one produced. A caller that clears with
     * `setSelectedCards([])`, or restores a different selection from somewhere else, has
     * ended it: a range from a card the caller no longer says is selected would reach
     * back across the clear and take in cards nobody pointed at.
     *
     * By what is in the selection rather than by which array holds it, so a caller that
     * stores a copy of what it was handed, or reorders it, is still holding it. That
     * leaves the two empty selections indistinguishable, which is why a gesture that
     * empties one reports no anchor at all.
     */
    const rangeStartFor = (current: readonly BoardSelectedCard[]): BoardSelectedCard | null => {
      if (!isSameSelection(current, reportedRef.current)) rangeStartRef.current = null

      return rangeStartRef.current
    }

    return {
      isSelected: card => selectedKeysRef.current.has(selectionKey(card)),
      getSelectedCards: () => latestRef.current.selectedCards,
      subscribe: listener => {
        listenersRef.current.add(listener)

        return () => {
          listenersRef.current.delete(listener)
        }
      },
      selectCard: (card, intent) => {
        const {selectedCards: current, itemIdsAt: cellItemIds} = latestRef.current
        const next = nextSelection({
          selectedCards: current,
          card,
          intent,
          rangeStart: rangeStartFor(current),
          itemIdsAt: cellItemIds,
        })

        report(next.selectedCards, next.rangeStart)
      },
      selectCards: (cards: readonly BoardSelectedCard[]) => {
        report(cards, cards[0] ?? null)
      },
      cellItemIds: (address: CellAddress) => latestRef.current.itemIdsAt(address),
    }
  }, [])

  return <BoardSelectionContext value={store}>{children}</BoardSelectionContext>
}
